/**
 * DateFilterSection Component
 * 날짜 범위 + 프리셋 버튼 필터
 */
import React, { useCallback, useMemo } from 'react';
import DateRangePicker from '@/shared/components/forms/DateRangePicker';
import { RiderWorkStatusFilters } from '../../types/riderWorkStatus.types';
import { formatDateToISO, removeDateHyphens } from '@/shared/utils/dateUtils';
import { formatDateToStorage } from '../../utils/dateFormatters';

/** 기간 프리셋 타입 */
type DatePreset = 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth';

interface DateFilterSectionProps {
    fromDate: string;
    toDate: string;
    onFilterChange: (key: keyof RiderWorkStatusFilters, value: string) => void;
}

const DateFilterSection: React.FC<DateFilterSectionProps> = ({ fromDate, toDate, onFilterChange }) => {
    /**
     * 날짜 변경 핸들러
     */
    const handleDateChange = (key: 'fromDate' | 'toDate', value: string) => {
        onFilterChange(key, removeDateHyphens(value));
    };

    /**
     * 기간 프리셋 버튼 클릭 핸들러
     */
    const handlePresetClick = useCallback(
        (preset: DatePreset) => {
            const today = new Date();
            let startDate: Date;
            let endDate: Date;

            switch (preset) {
                case 'thisWeek': {
                    // 이번 주 (월요일 ~ 오늘)
                    const dayOfWeek = today.getDay();
                    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
                    startDate = new Date(today);
                    startDate.setDate(today.getDate() + diffToMonday);
                    endDate = today;
                    break;
                }
                case 'lastWeek': {
                    // 지난 주 (월요일 ~ 일요일)
                    const dayOfWeek = today.getDay();
                    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
                    startDate = new Date(today);
                    startDate.setDate(today.getDate() + diffToMonday - 7);
                    endDate = new Date(startDate);
                    endDate.setDate(startDate.getDate() + 6);
                    break;
                }
                case 'thisMonth': {
                    // 이번 달 (1일 ~ 오늘)
                    startDate = new Date(today.getFullYear(), today.getMonth(), 1);
                    endDate = today;
                    break;
                }
                case 'lastMonth': {
                    // 지난 달 (1일 ~ 말일)
                    startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                    endDate = new Date(today.getFullYear(), today.getMonth(), 0);
                    break;
                }
            }

            onFilterChange('fromDate', formatDateToStorage(startDate));
            onFilterChange('toDate', formatDateToStorage(endDate));
        },
        [onFilterChange],
    );

    /**
     * 현재 선택된 프리셋 확인
     */
    const activePreset = useMemo((): DatePreset | null => {
        if (!fromDate || !toDate) return null;

        const today = new Date();
        const dayOfWeek = today.getDay();
        const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;

        // 이번 주 (월요일 ~ 오늘)
        const thisWeekStart = new Date(today);
        thisWeekStart.setDate(today.getDate() + diffToMonday);
        if (fromDate === formatDateToStorage(thisWeekStart) && toDate === formatDateToStorage(today)) {
            return 'thisWeek';
        }

        // 지난 주 (월요일 ~ 일요일)
        const lastWeekStart = new Date(today);
        lastWeekStart.setDate(today.getDate() + diffToMonday - 7);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
        if (fromDate === formatDateToStorage(lastWeekStart) && toDate === formatDateToStorage(lastWeekEnd)) {
            return 'lastWeek';
        }

        // 이번 달 (1일 ~ 오늘)
        const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        if (fromDate === formatDateToStorage(thisMonthStart) && toDate === formatDateToStorage(today)) {
            return 'thisMonth';
        }

        // 지난 달 (1일 ~ 말일)
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        if (fromDate === formatDateToStorage(lastMonthStart) && toDate === formatDateToStorage(lastMonthEnd)) {
            return 'lastMonth';
        }

        return null;
    }, [fromDate, toDate]);

    return (
        <div className="form-group date-range-group">
            <div className="form-field">
                <div className="form-label-row">
                    <label className="form-label">
                        <i className="fas fa-calendar-alt" />
                        조회 기간
                    </label>
                    {/* 기간 프리셋 버튼 */}
                    <div className="date-preset-buttons">
                        <button
                            type="button"
                            className={`date-preset-btn ${activePreset === 'thisWeek' ? 'active' : ''}`}
                            onClick={() => handlePresetClick('thisWeek')}
                        >
                            이번 주
                        </button>
                        <button
                            type="button"
                            className={`date-preset-btn ${activePreset === 'lastWeek' ? 'active' : ''}`}
                            onClick={() => handlePresetClick('lastWeek')}
                        >
                            지난 주
                        </button>
                        <button
                            type="button"
                            className={`date-preset-btn ${activePreset === 'thisMonth' ? 'active' : ''}`}
                            onClick={() => handlePresetClick('thisMonth')}
                        >
                            이번 달
                        </button>
                        <button
                            type="button"
                            className={`date-preset-btn ${activePreset === 'lastMonth' ? 'active' : ''}`}
                            onClick={() => handlePresetClick('lastMonth')}
                        >
                            지난 달
                        </button>
                    </div>
                </div>
                <DateRangePicker
                    startDate={formatDateToISO(fromDate)}
                    endDate={formatDateToISO(toDate)}
                    onStartDateChange={(value) => handleDateChange('fromDate', value)}
                    onEndDateChange={(value) => handleDateChange('toDate', value)}
                    startLabel="시작일"
                    endLabel="종료일"
                />
            </div>
        </div>
    );
};

export default DateFilterSection;
